import day from "dayjs";
import { sift } from "radash";
import { useRef } from "react";
import { useLocalStorage } from "react-use";

import { useProductOpportunitiesState } from "../product-opportunities";
import { convertRange } from "../convert-range";
import { useActivityCounts } from "../../../data/use-reports";
import { useFunnelOpportunityCounts } from "data/use-reports.js";
import { useNetworkingCounts } from "../../../data/use-reports";
import { useOpportunityCountsBy } from "../../../data/use-reports";
import { useProductOpportunityCountsBy } from "../../../data/use-reports";
import { useUserActivityCounts } from "../../../data/use-reports";
import { useUserOpportunityCountsBy } from "../../../data/use-reports";

import { AccumulatingOpportunityValue } from "./accumulating-opportunity-value";
import { OpportunityFunnel } from "./opportunity-funnel";
import { OpportunityWinRate } from "./opportunity-win-rate";
import { Totals } from "./totals";
import { UserActivities } from "../user-activities";

import {
  useDashboardDateFilters,
  USER_ACTIVITIES,
} from "../use-dashboard-filters";
import { useOrganization } from "../../../data/use-organization";
import { useOpportunityStates } from "../../../data/use-opportunity-states";

export const IconicMetrics = () => {
  const organization = useOrganization();

  const date = useRef(day());

  const loading = [
    useActivityCounts(),
    useActivityCounts(
      convertRange([date.current.subtract(1, "month"), date.current])
    ),
    useFunnelOpportunityCounts(),
    useNetworkingCounts(
      convertRange([date.current.subtract(1, "month"), date.current])
    ),
    useOpportunityCountsBy("stateId"),
    useProductOpportunityCountsBy("stateId"),
    useUserActivityCounts(
      convertRange([date.current.subtract(1, "month"), date.current])
    ),
    useUserOpportunityCountsBy("stateId"),
  ];

  const dateRange = useDashboardDateFilters("ICONIC");
  const userActivitiesRange = useDashboardDateFilters(USER_ACTIVITIES);

  const opportunityStates = useOpportunityStates();

  const potentialOpenStates = opportunityStates
    .all({ includeArchived: false })
    .filter(({ systemKey }) => systemKey !== "WON" && systemKey !== "LOST");

  const wonState = opportunityStates
    .all()
    .find(({ systemKey }) => systemKey === "WON");

  // TODO: implement the organization id
  const [currentStateIds, setCurrentStateIds] = useLocalStorage(
    `ICONIC.DASHBOARD.CURRENT_STATE_IDS`,
    potentialOpenStates.map(({ _id }) => _id)
  );

  const [forecastStateIds, setForecastStateIds] = useLocalStorage(
    `ICONIC.DASHBOARD.FORECAST_STATE_IDS`,
    potentialOpenStates.map(({ _id }) => _id)
  );

  const productOpportunitiesState = useProductOpportunitiesState(organization);

  if (sift(loading).length < loading.length) {
    return null;
  }

  return (
    <div
      style={{
        overflowY: "scroll",
      }}
    >
      <div className="mb-4 flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityFunnel
            title="Current Pipeline"
            {...{
              setSelectedStateIds: setForecastStateIds,
              selectedStateIds: forecastStateIds,
            }}
          />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityFunnel
            {...{
              setSelectedStateIds: setCurrentStateIds,
              selectedStateIds: currentStateIds,
            }}
            title="Opportunity Pipeline"
          />
        </div>
      </div>
      <div className="mb-4 flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <AccumulatingOpportunityValue
            {...{
              selectedStateIds: [wonState?._id],
            }}
            dateRange={dateRange}
          />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <AccumulatingOpportunityValue
            title="Company Forecast"
            {...{
              selectedStateIds: forecastStateIds,
            }}
            dateRange={dateRange}
          />
        </div>
      </div>
      <div className="mb-4 flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityWinRate
            dateRange={dateRange}
            {...{
              selectedStateIds: [wonState?._id],
            }}
            {...productOpportunitiesState}
          />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <OpportunityWinRate
            dateRange={dateRange}
            title="Company Forecast"
            factorType="value"
            {...{
              selectedStateIds: forecastStateIds,
            }}
            showFactoredValue
            {...productOpportunitiesState}
          />
        </div>
      </div>
      <div className="flex space-x-4">
        <div className="mr-4 w-1/2 space-y-4">
          <UserActivities dateRange={userActivitiesRange} />
        </div>
        <div className="mr-4 w-1/2 space-y-4">
          <Totals
            dateRange={dateRange}
            totalSelectedStateIds={[...forecastStateIds, ...currentStateIds]}
            subsetSelectedStateIds={[...forecastStateIds]}
          />
        </div>
      </div>
    </div>
  );
};
